import React, { useMemo, useState } from 'react';
import { Alert, IconButton, Stack, Tooltip } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import { getJourneySharingAdapter } from './journeySharingAdapters';

const ACTION_STYLES = {
  share: { backgroundColor: '#7457D9', hoverColor: '#6042C5' },
  copy: { backgroundColor: '#25A9A2', hoverColor: '#188E88' },
  print: { backgroundColor: '#F28B3C', hoverColor: '#DC7023' },
};

const actionButtonSx = ({ backgroundColor, hoverColor }) => ({
  width: { xs: 44, sm: 48 },
  height: { xs: 44, sm: 48 },
  color: '#FFFFFF',
  backgroundColor,
  boxShadow: '0 6px 16px rgba(45, 54, 72, 0.16)',
  '&:hover': {
    backgroundColor: hoverColor,
    transform: 'translateY(-1px)',
  },
  '&:focus-visible': {
    outline: '3px solid rgba(116, 87, 217, 0.28)',
    outlineOffset: 2,
  },
});

const JourneyRevisionShareActions = ({ revision, sharingAdapterId }) => {
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const adapterResolution = useMemo(() => {
    try {
      return { adapter: getJourneySharingAdapter(sharingAdapterId), error: '' };
    } catch (adapterError) {
      return {
        adapter: null,
        error: adapterError?.message || 'Sharing is not available for this brief.',
      };
    }
  }, [sharingAdapterId]);
  const adapter = adapterResolution.adapter;

  const runAction = async (action) => {
    setFeedback('');
    setError('');
    try {
      const payload = adapter.buildPayload(revision);
      const result = await adapter[action](payload);
      if (action === 'share') {
        setFeedback(result.method === 'clipboard' ? 'Share is not available here. Copied instead.' : 'Brief shared.');
      } else if (action === 'copy') {
        setFeedback('Brief copied.');
      } else {
        setFeedback(result === false ? 'Print window was blocked.' : 'Print window opened.');
      }
    } catch (actionError) {
      if (actionError?.name !== 'AbortError') {
        setError(actionError?.message || 'This action could not be completed.');
      }
    }
  };

  if (!adapter) {
    return <Alert severity="error" sx={{ mt: 0.8, py: 0 }}>{adapterResolution.error}</Alert>;
  }

  return (
    <>
      <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
        <Tooltip title="Share brief">
          <IconButton aria-label="Share" onClick={() => runAction('share')} sx={actionButtonSx(ACTION_STYLES.share)}>
            <IosShareRoundedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Copy brief">
          <IconButton aria-label="Copy" onClick={() => runAction('copy')} sx={actionButtonSx(ACTION_STYLES.copy)}>
            <ContentCopyRoundedIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Print brief">
          <IconButton aria-label="Print" onClick={() => runAction('print')} sx={actionButtonSx(ACTION_STYLES.print)}>
            <PrintRoundedIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      {feedback ? <Alert severity="success" sx={{ mt: 0.8, py: 0 }}>{feedback}</Alert> : null}
      {error ? <Alert severity="error" sx={{ mt: 0.8, py: 0 }}>{error}</Alert> : null}
    </>
  );
};

export default JourneyRevisionShareActions;
